import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { PenLine, Palette } from 'lucide-react'
import { CATEGORY_LABEL, isLead } from '../lib'
import type { ThreadCategory, ThreadSummary } from '../types'

export function CategoryBadge({ category, score }: { category: ThreadCategory | null; score: number | null }) {
  if (!category) return <Badge variant="outline">Reading…</Badge>
  const lead = isLead({ category, commissionScore: score })
  return (
    <Badge
      variant={lead ? 'default' : category === 'spam' ? 'destructive' : 'secondary'}
      className={cn(lead && 'bg-amber-500 hover:bg-amber-500 text-white')}
    >
      {lead && category !== 'commission' ? 'Possible commission' : CATEGORY_LABEL[category]}
      {lead && score !== null ? ` · ${Math.round(score * 100)}%` : ''}
    </Badge>
  )
}

function when(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

interface Props {
  threads: ThreadSummary[] | undefined
  isLoading: boolean
  selectedId: number | null
  onSelect: (id: number) => void
  emptyText: string
  /** The Sent tab names who it went to rather than who it came from. */
  showRecipient?: boolean
}

export function ThreadList({ threads, isLoading, selectedId, onSelect, emptyText, showRecipient }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }
  if (!threads?.length) {
    return <p className="p-6 text-center text-sm text-muted-foreground">{emptyText}</p>
  }
  return (
    <ul className="divide-y">
      {threads.map((t) => (
        <li key={t.id}>
          <button
            onClick={() => onSelect(t.id)}
            className={cn(
              'w-full space-y-1 px-4 py-3 text-left transition-colors hover:bg-muted/60',
              selectedId === t.id && 'bg-muted',
              !t.read && 'border-l-2 border-l-primary'
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={cn('truncate text-sm', !t.read && 'font-semibold')}>
                {showRecipient ? 'To: ' : ''}
                {t.contactName || t.contactEmail || 'Unknown sender'}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{when(t.lastMessageAt)}</span>
            </div>
            <div className={cn('truncate text-sm', !t.read ? 'text-foreground' : 'text-muted-foreground')}>
              {t.subject || '(no subject)'}
            </div>
            {t.summary && <p className="line-clamp-2 text-xs text-muted-foreground">{t.summary}</p>}
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {/* A conversation the artist started has nothing to triage until someone answers. */}
              {(t.category || t.lastDirection !== 'outbound') && <CategoryBadge category={t.category} score={t.commissionScore} />}
              {t.urgency === 'high' && <Badge variant="destructive">Time-sensitive</Badge>}
              {t.hasDraft && (
                <Badge variant="outline" className="gap-1">
                  <PenLine className="h-3 w-3" /> Draft ready
                </Badge>
              )}
              {t.commissionId && (
                <Badge variant="outline" className="gap-1">
                  <Palette className="h-3 w-3" /> Commission
                </Badge>
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}
